// src/components/Lms/Pages/MisCertificados.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    FiAward,
    FiDownload,
    FiCalendar,
    FiBookOpen,
    FiExternalLink,
    FiX,
    FiEye,
} from "react-icons/fi";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { API_BASE } from "../../../api";
import { Models } from "../Utils/ModelsLMS";
import { getSessionUserId } from "../../../utils/SessionUser";
import AlertDialog from "../Components/Shared/AlertDialog";

const MisCertificados = () => {
    const [certificados, setCertificados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCertificate, setSelectedCertificate] = useState(null);

    // Filtros
    const [tab, setTab] = useState("todo");
    const [searchTerm, setSearchTerm] = useState("");

    // Estado para modal de alerta
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    useEffect(() => {
        const loadCertificados = async () => {
            setLoading(true);
            setError(null);
            try {
                // 1. Obtener el ID del estudiante directamente usando el endpoint optimizado
                const idUsuario = getSessionUserId();

                if (!idUsuario) {
                    setError("No se pudo identificar al usuario en sesión.");
                    setCertificados([]);
                    setLoading(false);
                    return;
                }

                // Paso 1: Obtener Estudiante directamente por IdUsuario
                const resEstudiante = await axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuario));
                // Manejar posible respuesta vacía o error si no existe
                const estudiante = resEstudiante.data;
                const idEstudiante = estudiante?.idEstudiante ?? estudiante?.IdEstudiante ?? null;

                if (!idEstudiante) {
                    setError("No se encontró un perfil de estudiante asociado a tu usuario.");
                    setCertificados([]);
                    setLoading(false);
                    return;
                }

                console.log('[MisCertificados] ID Estudiante resuelto:', idEstudiante);

                // Paso 2: Obtener AMBOS tipos de certificaciones en paralelo
                const [resCursos, resCarreras] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.estudianteCertificacionesDetalles(idEstudiante)),
                    axios.get(LMS_ENDPOINTS.estudianteCertificacionesCarreraByEstudiante(idEstudiante))
                ]);

                const dataCursos = resCursos.data || [];
                const dataCarreras = resCarreras.data || [];

                // Mapear Certificados de CURSOS
                const certsCursos = dataCursos.map((cert) => {
                    // Normalizar fecha
                    let fAsignacion = cert.fechaAsignacion || cert.FechaAsignacion;
                    if (fAsignacion && typeof fAsignacion === 'string' && fAsignacion.includes('T') && !fAsignacion.endsWith('Z')) {
                        fAsignacion += 'Z';
                    }

                    return {
                        id: `curso-${cert.idEstudiantesCertificaciones || cert.IdEstudiantesCertificaciones}`,
                        tipo: 'curso',
                        nombreItem: cert.nombreCurso || cert.NombreCurso,
                        nombreCertificacion: cert.nombreCertificacion || cert.NombreCertificacion || "Certificado de Finalización",
                        fechaAsignacion: fAsignacion,
                        rutaCertificadoURL: cert.rutaCertificadoURL || cert.RutaCertificadoURL,
                        imagenItem: cert.imagenCurso || cert.ImagenCurso || "",
                        idReferencia: cert.idCurso || cert.IdCurso,
                        empresaNombre: cert.empresaNombre || cert.EmpresaNombre || "General"
                    };
                });

                // Mapear Certificados de CARRERAS
                const certsCarreras = dataCarreras.map((cert) => {
                    let fAsignacion = cert.fechaAsignacion || cert.FechaAsignacion;
                    if (fAsignacion && typeof fAsignacion === 'string' && fAsignacion.includes('T') && !fAsignacion.endsWith('Z')) {
                        fAsignacion += 'Z';
                    }

                    return {
                        id: `carrera-${cert.idEstudianteCertificacionCarrera || cert.IdEstudianteCertificacionCarrera}`,
                        tipo: 'carrera',
                        nombreItem: cert.nombreCarrera || cert.NombreCarrera || "Carrera Sin Nombre",
                        nombreCertificacion: "Certificado de Carrera Profesional",
                        fechaAsignacion: fAsignacion,
                        rutaCertificadoURL: cert.imagenUrl || cert.ImagenUrl || cert.RutaCertificadoURL || cert.rutaCertificadoURL, // Nota: el DTO/SP usa ImagenUrl
                        imagenItem: cert.urlImagenCarrera || cert.UrlImagenCarrera || "",
                        idReferencia: cert.idCarrera || cert.IdCarrera,
                        empresaNombre: cert.empresaNombre || cert.EmpresaNombre || "General"
                    };
                });

                // Combinar y ordenar
                const todos = [...certsCursos, ...certsCarreras].sort((a, b) => {
                    // Ordenar por fecha más reciente primero
                    const dateA = new Date(a.fechaAsignacion || 0);
                    const dateB = new Date(b.fechaAsignacion || 0);
                    return dateB - dateA;
                });

                setCertificados(todos);
            } catch (error) {
                console.error("Error al cargar certificados:", error);
                // Si el error es 404 en estudianteByUsuario, es que no es estudiante
                if (error.response && error.response.status === 404) {
                    setError("No se encontró un perfil de estudiante asociado.");
                } else {
                    setError("Ocurrió un error al cargar tus certificados. Por favor, intenta de nuevo.");
                }
                setCertificados([]);
            } finally {
                setLoading(false);
            }
        };

        loadCertificados();
    }, []);

    const handleViewCertificate = (certificado) => {
        setSelectedCertificate(certificado);
        setModalOpen(true);
    };

    const handleDownloadCertificate = async (certificado) => {
        if (!certificado.rutaCertificadoURL) {
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'No hay URL de certificado disponible.',
                variant: 'error'
            });
            return;
        }

        // Construir la URL completa
        const fullUrl = certificado.rutaCertificadoURL.startsWith("http")
            ? certificado.rutaCertificadoURL
            : `${SERVER_URL}${certificado.rutaCertificadoURL}`;

        // Detectar extensión del archivo
        const extension = certificado.rutaCertificadoURL.split('.').pop().toLowerCase();
        const fileName = `Certificado_${certificado.tipo}_${certificado.nombreItem.replace(/\s+/g, '_')}.${extension}`;

        try {
            // Intentar descargar usando fetch y blob (esto permite forzar el nombre del archivo)
            // Agregamos cache: 'no-cache' para evitar problemas con CORS y preflights cacheados
            const response = await fetch(fullUrl, {
                mode: 'cors',
                cache: 'no-cache'
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Liberar el blob URL
            window.URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.warn("[MisCertificados] Falló descarga por Blob (posible CORS), intentando descarga directa...", error);

            // Fallback: Abrir en pestaña nueva o intentar descarga directa via tag <a>
            // Esto evita el error de CORS que sucede con fetch(), aunque no garantiza el nombre del archivo
            try {
                const link = document.createElement('a');
                link.href = fullUrl;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                // El atributo download no siempre funciona cross-origin sin CORS, 
                // pero al menos abre el archivo para que el usuario guarde manualmente
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (fallbackError) {
                console.error("[MisCertificados] Error en fallback de descarga:", fallbackError);
                window.open(fullUrl, '_blank');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        try {
            // Si la fecha ya viene con la 'Z' (por el map anterior) o es un ISO puro YYYY-MM-DD
            // JS lo tratará correctamente como UTC y lo pasará a local al formatear
            const date = new Date(dateString);
            return date.toLocaleDateString("es-MX", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return "—";
        }
    };

    // Filtrado
    const filtrados = certificados.filter(cert => {
        // Filtro por Tab
        if (tab !== 'todo' && cert.tipo !== tab) return false;

        // Filtro por Nombre (Búsqueda)
        if (searchTerm && !cert.nombreItem.toLowerCase().includes(searchTerm.toLowerCase())) return false;

        return true;
    });

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1>Mis Certificados</h1>
                <p style={{ color: "#64748b", marginTop: "0.5rem" }}>
                    Aquí puedes ver y descargar todos tus certificados obtenidos
                </p>
            </div>

            {/* Estadísticas Compactas con Línea Primaria */}
            <div className="stats-grid stats-grid-wrap" style={{ marginBottom: '2rem' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiAward size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Total de Certificados</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{certificados.length}</p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiBookOpen size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Cursos Completados</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{certificados.filter(c => c.tipo === 'curso').length}</p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiCalendar size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Carreras Completadas</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{certificados.filter(c => c.tipo === 'carrera').length}</p>
                    </div>
                </div>
            </div>

            {/* UI de Filtros (Tabs + Búsqueda) */}
            <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))', marginTop: '1.5rem' }}>
                {/* 1. Header (Tabs) */}
                <div style={{
                    display: 'flex',
                    width: '100%',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden',
                    padding: '0.4rem',
                    gap: '0.5rem',
                    border: '1px solid #e2e8f0',
                    borderBottom: 'none'
                }}>
                    {['todo', 'carrera', 'curso'].map((option) => (
                        <div
                            key={option}
                            onClick={() => setTab(option)}
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '0.4rem 0',
                                fontSize: '0.9rem',
                                fontWeight: tab === option ? 700 : 500,
                                color: tab === option ? 'var(--color-primary-dark)' : '#64748b',
                                backgroundColor: tab === option ? '#ffffff' : 'transparent',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none',
                                textTransform: 'capitalize',
                                boxShadow: tab === option ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                        >
                            {option === 'todo' ? 'Todo' : (option === 'carrera' ? 'Carreras' : 'Cursos')}
                        </div>
                    ))}
                </div>

                {/* 2. Body (Search) */}
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '0.8rem 1rem',
                }}>
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '999px',
                        padding: '0.4rem 1rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        <FiEye style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} /> {/* Using FiEye as general icon, could be FiSearch */}
                        <input
                            type="text"
                            placeholder="Buscar certificados por nombre..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
                        />
                    </div>
                </div>
            </div>

            {/* Contenido principal */}
            {loading ? (
                <div className="catalog-loading">Cargando certificados...</div>
            ) : error ? (
                <div
                    style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#ef4444",
                        backgroundColor: "#fef2f2",
                        borderRadius: "8px",
                        border: "1px solid #fecaca",
                    }}
                >
                    {error}
                </div>
            ) : filtrados.length === 0 ? (
                <div
                    style={{
                        padding: "3rem",
                        textAlign: "center",
                        color: "#94a3b8",
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                    }}
                >
                    <FiAward size={48} style={{ marginBottom: "1rem", opacity: 0.5 }} />
                    <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                        No se encontraron certificados
                    </p>
                    <p style={{ fontSize: "0.9rem" }}>
                        {searchTerm ? "Intenta con otra búsqueda" : "Completa tus cursos o carreras para obtener certificados"}
                    </p>
                </div>
            ) : (
                <div className="certificates-grid">
                    {filtrados.map((cert) => {
                        const hasCertificate = !!cert.rutaCertificadoURL;
                        const isCarrera = cert.tipo === 'carrera';

                        return (
                            <article
                                key={cert.id}
                                className="certificate-card"
                            >
                                <div className="certificate-header" style={{
                                    background: isCarrera
                                        ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                }}>
                                    {hasCertificate ? (
                                        <div className="certificate-image">
                                            <img
                                                src={cert.rutaCertificadoURL.startsWith('http') ? cert.rutaCertificadoURL : `${SERVER_URL}${cert.rutaCertificadoURL}`}
                                                alt={`Certificado de ${cert.nombreItem}`}
                                            />
                                        </div>
                                    ) : (
                                        <div className="certificate-image certificate-image-placeholder">
                                            <FiAward size={48} />
                                        </div>
                                    )}
                                </div>

                                <div className="certificate-body">
                                    <div className="certificate-badge" style={{
                                        background: isCarrera
                                            ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}>
                                        <FiAward size={16} />
                                        <span>{isCarrera ? 'Certificado de Carrera' : 'Certificado de Curso'}</span>
                                    </div>

                                    <h3 className="certificate-title">{cert.nombreItem}</h3>
                                    <p className="certificate-subtitle">
                                        {cert.nombreCertificacion}
                                    </p>

                                    <div className="certificate-meta">
                                        <div className="certificate-meta-item">
                                            <FiCalendar size={14} />
                                            <span>Obtenido el {formatDate(cert.fechaAsignacion)}</span>
                                        </div>
                                        <div className="certificate-meta-item" style={{ marginTop: '0.3rem', fontWeight: '500', color: '#1e293b' }}>
                                            <span style={{ fontSize: '0.9rem' }}>🏢 {cert.empresaNombre}</span>
                                        </div>
                                    </div>

                                    <div className="certificate-actions">
                                        <button
                                            type="button"
                                            className="certificate-btn certificate-btn-primary"
                                            onClick={() => handleViewCertificate(cert)}
                                            disabled={!cert.rutaCertificadoURL}
                                            style={isCarrera ? { background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' } : {}}
                                        >
                                            <FiEye size={16} />
                                            Ver Certificado
                                        </button>
                                        <button
                                            type="button"
                                            className="certificate-btn certificate-btn-download"
                                            onClick={() => handleDownloadCertificate(cert)}
                                            disabled={!cert.rutaCertificadoURL}
                                            title="Descargar certificado"
                                        >
                                            <FiDownload size={18} />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* Modal para visualizar certificado */}
            {modalOpen && selectedCertificate && (
                <div
                    className="certificate-modal-overlay"
                    onClick={() => setModalOpen(false)}
                >
                    <div
                        className="certificate-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="certificate-modal-close"
                            onClick={() => setModalOpen(false)}
                        >
                            <FiX size={24} />
                        </button>
                        <h3 className="certificate-modal-title">
                            {selectedCertificate.nombreItem}
                        </h3>
                        <div className="certificate-modal-body">
                            <img
                                src={selectedCertificate.rutaCertificadoURL.startsWith('http') ? selectedCertificate.rutaCertificadoURL : `${SERVER_URL}${selectedCertificate.rutaCertificadoURL}`}
                                alt={`Certificado de ${selectedCertificate.nombreItem}`}
                                className="certificate-modal-image"
                            />
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .certificates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .certificate-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
        }

        .certificate-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-4px);
        }

        .certificate-header {
          position: relative;
          height: 180px;
          overflow: hidden;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .certificate-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .certificate-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .certificate-image-placeholder {
          color: white;
          opacity: 0.8;
        }

        .certificate-body {
          padding: 1.5rem;
        }

        .certificate-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.25rem 0.75rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .certificate-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.5rem 0;
          line-height: 1.3;
        }

        .certificate-subtitle {
          font-size: 0.9rem;
          color: #64748b;
          margin: 0 0 1rem 0;
        }

        .certificate-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .certificate-meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #64748b;
        }

        .certificate-actions {
          display: flex;
          gap: 0.5rem;
        }

        .certificate-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .certificate-btn-primary {
          background: linear-gradient(135deg, #c92a2a 0%, #a61e1e 100%);
          color: white;
        }

        .certificate-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(201, 42, 42, 0.4);
        }

        .certificate-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .certificate-btn-download {
          flex: 0 0 auto;
          width: 48px;
          background: #f1f5f9;
          color: #475569;
          padding: 0.75rem;
        }

        .certificate-btn-download:hover:not(:disabled) {
          background: #e2e8f0;
          transform: translateY(-2px);
        }

        .certificate-btn-download:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Modal Styles */
        .certificate-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1rem;
        }

        .certificate-modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .certificate-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }

        .certificate-modal-close:hover {
          background: #f1f5f9;
          transform: scale(1.1);
        }

        .certificate-modal-title {
          padding: 1.5rem 1.5rem 1rem;
          margin: 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          border-bottom: 1px solid #e2e8f0;
        }

        .certificate-modal-body {
          flex: 1;
          padding: 1rem;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1e293b;
        }

        .certificate-modal-image {
          max-width: 100%;
          max-height: 70vh;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        @media (max-width: 768px) {
          .certificates-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

            {/* Modal de alerta */}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />
        </div>
    );
};


export default MisCertificados;
